/**
 * @fileoverview The plant check-up's status as a query, read by the week-later question
 * on the Plants page (DoctorMoments): whether a check-up can be had
 * now (the trial and Grow More; never Free), and the "Did it turn out to be
 * …?" questions due.
 *
 * Read only for a signed-in gardener; refetched after a check-up so the
 * questions stay true.
 *
 * Where: the Plants page's week-later question (DoctorMoments).
 * API: GET /plant-doctor/status (getDoctorStatus).
 * Settings: PLANT_DOCTOR_STATUS_QUERY_KEY, PLANT_DOCTOR_STATUS_STALE_MS
 * (config/tunables/plantList).
 *
 * @module useDoctorStatus
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { getDoctorStatus, type DoctorStatus } from '@/shared/api/diagnose';
import { useAuth } from '@/shared/hooks/useAuth';
import { PLANT_DOCTOR_STATUS_QUERY_KEY, PLANT_DOCTOR_STATUS_STALE_MS } from '@/config/tunables/plantList';

/**
 * The check-up's status, once signed in.
 *
 * @param enabled - Read it at all (false to skip, e.g. while billing says AI is available anyway).
 * @returns The query.
 */
export function useDoctorStatus(enabled = true): UseQueryResult<DoctorStatus, Error> {
  const { mysqlUser } = useAuth();
  return useQuery({
    queryKey: PLANT_DOCTOR_STATUS_QUERY_KEY,
    queryFn: getDoctorStatus,
    enabled: enabled && Boolean(mysqlUser),
    staleTime: PLANT_DOCTOR_STATUS_STALE_MS,
  });
}
